import { Snackbar } from '@/components/shared/Snackbar/Snackbar';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { containedButtonStyle } from '@/theme';
import { getStructureRedirectPath } from '@/utils/structureRedirectPath';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useDrawerWidths } from '../../hooks/useDrawerWidths';
import { AssemblyTemplate } from './AssemblyTemplate';
import { BackButton } from './BackButton';
import { DefectsLegendTemplate } from './DefectsLegendTemplate';
import { GenericTemplate } from './GenericTemplate';
import { PoiTemplate } from './PoiTemplate';
import { QuerySummaryTemplate } from './QuerySummaryTemplate';
import { StructureTemplate } from './StructureTemplate';
import { VersionsDrawer } from './VersionsDrawer';
import { FormTypes } from './constants';
import { Permissions } from '@/types';
import {
  AllPointOfInterestTemplateDocument,
  AllTemplatesForBuilderDocument,
  AllLogsTemplatesByTypeDocument,
  StructureStatus,
  useStructureForTemplateBuilderQuery,
} from '@/__generated__/graphql';
import {
  selectedPOIType as selectedPOITemplate,
  selectedTemplateId,
  selectedWorklogsTemplateId,
  snackbarMessage as snackbarMessageState,
} from './state';
import { RiskConfigurationTemplate } from './RiskConfigurationTemplate';
import { LogsTemplatesList } from './Logs/LogsTemplatesList';

const versionEnabledTypes = new Set([
  FormTypes.PointOfInterest,
  FormTypes.DefectLegends,
  FormTypes.Assembly,
  FormTypes.QuerySummary,
  FormTypes.RiskConfiguration,
  FormTypes.Logs,
]);

export const TemplatesList = ({ structureId }: { structureId: string }) => {
  const { rightDrawerWidth } = useDrawerWidths();
  const isAdmin = useFeatureFlag('admin');
  const isBetaUser = useFeatureFlag('beta-user');
  const canViewRiskConfiguration = useHavePermission(Permissions.MANAGE_RISK_CONFIGURATION);

  const [structureName, setStructureName] = useState<string>('');
  const [structureStatus, setStructureStatus] = useState<StructureStatus>();
  const selectedPoiType = useAtomValue(selectedPOITemplate);
  const structureTemplate = useAtomValue(selectedTemplateId);
  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);

  const [selectedTemplateType, setSelectedTemplateType] = useState<FormTypes>(
    isAdmin ? FormTypes.Assembly : FormTypes.RiskConfiguration
  );

  const [value, setValue] = useState(Object.values(FormTypes).indexOf(selectedTemplateType));

  const [snackbarMessage, setSnackbarMessage] = useAtom(snackbarMessageState);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const { shouldShow, content } = snackbarMessage;

  const { data: structureNameData } = useStructureForTemplateBuilderQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    setStructureName(structureNameData?.structure?.name ?? '');
    setStructureStatus(structureNameData?.structure?.status);
  }, [structureNameData?.structure?.name, structureNameData?.structure?.status]);

  const handleStructureNavigation = useCallback(() => {
    const pathname = getStructureRedirectPath(
      { id: structureId, status: structureStatus! },
      isBetaUser
    );

    window.open(pathname, '_blank');
  }, [isBetaUser, structureId, structureStatus]);

  const formTypesMap = useMemo(() => {
    return {
      [FormTypes.PointOfInterest]: {
        collectionName: 'PointOfInterestTemplate',
        referenceId: selectedPoiType?.id ?? '',
        allowRestore: true,
        name: 'Point Of Interest',
        disabled: !isAdmin,
      },
      [FormTypes.DefectLegends]: {
        collectionName: 'Structure',
        referenceId: structureId,
        allowRestore: false,
        name: 'Defect Legends',
        disabled: !isAdmin,
      },
      [FormTypes.Assembly]: {
        collectionName: 'Template',
        referenceId: structureTemplate,
        allowRestore: true,
        name: 'Assembly',
        disabled: !isAdmin,
      },
      [FormTypes.PaintBlocks]: {
        collectionName: 'PaintBlock',
        referenceId: '',
        allowRestore: false,
        name: 'Paint Block',
        disabled: true,
      },
      [FormTypes.Structure]: {
        collectionName: 'Structure',
        referenceId: structureId,
        allowRestore: false,
        name: 'Structure',
        disabled: !isAdmin,
      },
      [FormTypes.QuerySummary]: {
        collectionName: 'Template',
        referenceId: structureTemplate,
        allowRestore: true,
        name: 'Query Summary',
        disabled: !isAdmin,
      },
      [FormTypes.RiskConfiguration]: {
        collectionName: 'RiskConfiguration',
        referenceId: structureId,
        allowRestore: false,
        name: 'Risk Configuration',
        disabled: !isAdmin && !canViewRiskConfiguration,
      },
      [FormTypes.Logs]: {
        collectionName: 'LogsTemplate',
        referenceId: worklogsTemplateId ?? '',
        allowRestore: true,
        name: 'Logs',
        disabled: !isAdmin,
      },
    };
  }, [
    canViewRiskConfiguration,
    isAdmin,
    selectedPoiType?.id,
    structureId,
    structureTemplate,
    worklogsTemplateId,
  ]);

  const handleTabsChange = useCallback(
    (_event: SyntheticEvent, newValue: number) => {
      setValue(newValue);
      const enabledFormTypes = Object.values(FormTypes).filter(
        (type) => !formTypesMap[type].disabled
      );

      setSelectedTemplateType(enabledFormTypes[newValue]);
    },
    [formTypesMap]
  );

  return (
    <>
      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
      <Stack width="100%">
        <Box
          sx={{
            overflowY: 'scroll',
            height: '100vh',
            padding: '2.4rem',
            paddingBottom: '10rem',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            marginRight: versionEnabledTypes.has(selectedTemplateType)
              ? `${rightDrawerWidth}px`
              : '0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction="row"
            gap={3}
            sx={{ display: 'flex', alignItem: 'center', pb: 2, mt: 2 }}
          >
            <BackButton />
            <Typography variant="h6" sx={{ pt: 1 }}>
              {structureName}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Button
                startIcon={<ExitToAppIcon />}
                variant="contained"
                sx={containedButtonStyle}
                onClick={handleStructureNavigation}
              >
                Go to {structureName}
              </Button>
            </Stack>
          </Stack>
          <Stack
            gap={3}
            direction="row"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: (theme) => theme.zIndex.drawer,
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Tabs
              value={value}
              onChange={handleTabsChange}
              sx={{ '& button': { textTransform: 'none', textWrap: 'nowrap' } }}
              selectionFollowsFocus
            >
              {Object.values(FormTypes)
                .filter((type) => !formTypesMap[type].disabled)
                .map((type) => (
                  <Tab
                    key={type}
                    sx={{ flex: 1 }}
                    label={formTypesMap[type].name}
                    disabled={false}
                  />
                ))}
            </Tabs>
          </Stack>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
            }}
          >
            {selectedTemplateType === FormTypes.Assembly && (
              <AssemblyTemplate structureId={structureId} structureName={structureName} />
            )}
            {selectedTemplateType === FormTypes.QuerySummary && (
              <QuerySummaryTemplate structureId={structureId} structureName={structureName} />
            )}
            {selectedTemplateType === FormTypes.PointOfInterest && (
              <PoiTemplate structureId={structureId} structureName={structureName} />
            )}
            {selectedTemplateType === FormTypes.DefectLegends && (
              <DefectsLegendTemplate structureId={structureId} structureName={structureName} />
            )}
            {selectedTemplateType === FormTypes.Structure && (
              <StructureTemplate structureId={structureId} />
            )}
            {selectedTemplateType === FormTypes.RiskConfiguration && (
              <RiskConfigurationTemplate structureId={structureId} />
            )}
            {selectedTemplateType === FormTypes.Logs && (
              <LogsTemplatesList structureId={structureId} currentStructureName={structureName} />
            )}
            {![
              FormTypes.PointOfInterest,
              FormTypes.DefectLegends,
              FormTypes.Assembly,
              FormTypes.QuerySummary,
              FormTypes.Structure,
              FormTypes.Logs,
            ].includes(selectedTemplateType) && (
              <GenericTemplate
                structureId={structureId}
                selectedTemplateType={selectedTemplateType.toString()}
              />
            )}
          </Box>
        </Box>
        {versionEnabledTypes.has(selectedTemplateType) && (
          <VersionsDrawer
            data-testid="versions-drawer"
            structureId={structureId}
            collectionName={formTypesMap[selectedTemplateType].collectionName}
            referenceId={formTypesMap[selectedTemplateType].referenceId}
            refetchDocuments={[
              AllPointOfInterestTemplateDocument,
              AllTemplatesForBuilderDocument,
              AllLogsTemplatesByTypeDocument,
            ]}
            disableClose
            allowRestore={formTypesMap[selectedTemplateType].allowRestore}
          />
        )}
      </Stack>
    </>
  );
};
